import React, { useState, useEffect, useCallback } from "react";
import { Database, Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METIERS = [
  "Plombier", "Sanitaire", "Chauffagiste", "Électricien", "Serrurier",
  "Menuisier", "Peintre", "Plâtrier", "Carreleur", "Maçon",
  "Façadier", "Couvreur", "Ferblantier", "Vitrier", "Paysagiste",
  "Photovoltaïque", "Rénovation", "Autre",
];
const CANTONS = ["Tous", "Vaud", "Genève", "Fribourg", "Valais", "Neuchâtel", "Jura", "Berne"];

export const BenchmarksSection = () => {
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState({ metier: "", canton: "Tous", cpl_moyen: "", taux_qualification: "", taux_rdv: "", taux_signature: "", panier_moyen: "" });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config/benchmarks`);
      setBenchmarks(res.data.benchmarks || []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!newRow.metier) return;
    try {
      await axios.post(`${API}/admin/config/benchmarks`, {
        ...newRow,
        cpl_moyen: newRow.cpl_moyen ? parseFloat(newRow.cpl_moyen) : null,
        taux_qualification: newRow.taux_qualification ? parseFloat(newRow.taux_qualification) : null,
        taux_rdv: newRow.taux_rdv ? parseFloat(newRow.taux_rdv) : null,
        taux_signature: newRow.taux_signature ? parseFloat(newRow.taux_signature) : null,
        panier_moyen: newRow.panier_moyen ? parseInt(newRow.panier_moyen) : null,
      });
      setNewRow({ metier: "", canton: "Tous", cpl_moyen: "", taux_qualification: "", taux_rdv: "", taux_signature: "", panier_moyen: "" });
      load();
    } catch { /* */ }
  };

  const remove = async (metier, canton) => {
    try {
      await axios.delete(`${API}/admin/config/benchmarks/${encodeURIComponent(metier)}/${encodeURIComponent(canton)}`);
      load();
    } catch { /* */ }
  };

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="benchmarks-section">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Database className="w-5 h-5 text-primary" /> Benchmarks Métier
      </h2>
      <p className="text-xs text-muted-foreground mb-4">Référentiel par métier/canton pour des estimations plus précises dans le diagnostic.</p>

      {/* Existing */}
      {benchmarks.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-muted-foreground">
              <th className="pb-2 pr-2">Métier</th><th className="pb-2 pr-2">Canton</th>
              <th className="pb-2 pr-2">CPL (CHF)</th><th className="pb-2 pr-2">Qualif %</th>
              <th className="pb-2 pr-2">RDV %</th><th className="pb-2 pr-2">Signature %</th>
              <th className="pb-2 pr-2">Panier</th><th className="pb-2"></th>
            </tr></thead>
            <tbody>
              {benchmarks.map((b, i) => (
                <tr key={i} className="border-b border-muted/50">
                  <td className="py-2 pr-2 font-medium">{b.metier}</td>
                  <td className="py-2 pr-2">{b.canton}</td>
                  <td className="py-2 pr-2">{b.cpl_moyen ?? "—"}</td>
                  <td className="py-2 pr-2">{b.taux_qualification ?? "—"}</td>
                  <td className="py-2 pr-2">{b.taux_rdv ?? "—"}</td>
                  <td className="py-2 pr-2">{b.taux_signature ?? "—"}</td>
                  <td className="py-2 pr-2">{b.panier_moyen ?? "—"}</td>
                  <td className="py-2">
                    <button onClick={() => remove(b.metier, b.canton)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div>
          <label className="text-xs font-medium">Métier</label>
          <select value={newRow.metier} onChange={(e) => setNewRow((p) => ({ ...p, metier: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-xs">
            <option value="">Choisir...</option>
            {METIERS.map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Canton</label>
          <select value={newRow.canton} onChange={(e) => setNewRow((p) => ({ ...p, canton: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-xs">
            {CANTONS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className="text-xs font-medium">CPL (CHF)</label><Input value={newRow.cpl_moyen} onChange={(e) => setNewRow((p) => ({ ...p, cpl_moyen: e.target.value }))} placeholder="45" className="h-8 text-xs" /></div>
        <div><label className="text-xs font-medium">Qualif %</label><Input value={newRow.taux_qualification} onChange={(e) => setNewRow((p) => ({ ...p, taux_qualification: e.target.value }))} placeholder="60" className="h-8 text-xs" /></div>
        <div><label className="text-xs font-medium">RDV %</label><Input value={newRow.taux_rdv} onChange={(e) => setNewRow((p) => ({ ...p, taux_rdv: e.target.value }))} placeholder="40" className="h-8 text-xs" /></div>
        <div><label className="text-xs font-medium">Signature %</label><Input value={newRow.taux_signature} onChange={(e) => setNewRow((p) => ({ ...p, taux_signature: e.target.value }))} placeholder="33" className="h-8 text-xs" /></div>
        <div><label className="text-xs font-medium">Panier CHF</label><Input value={newRow.panier_moyen} onChange={(e) => setNewRow((p) => ({ ...p, panier_moyen: e.target.value }))} placeholder="3000" className="h-8 text-xs" /></div>
        <Button size="sm" onClick={save} disabled={!newRow.metier}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
    </div>
  );
};
